/**
 * Krok UXV4a bod 4 → krok UXF1 bod 2 — OBRAZEK NEMOVITOSTI. JEDNA FUNKCE,
 * JEDNO PORADI.
 *
 * Predloha `docs/design/ux-v4/DESIGN-UX-V4.md` § Pravidla 3 zada obrazek
 * nemovitosti vsude, kde se s nemovitosti pracuje (Dnesek, seznamy, detail,
 * kanban, inzerat), a zakazuje stred obce. Poradi zdroju je od kroku UXF1:
 *
 *   1. FOTKA zakazky/nemovitosti,
 *   2. PANORAMA Mapy.com u adresy (serverova proxy `…/cover/panorama.jpg`),
 *   3. LETECKY SNIMEK Mapy.com s vektorovou hranici parcely
 *      (serverova proxy `…/cover/aerial.jpg`, retina 2x),
 *   4. VETA „Fotka po nahrání".
 *
 * Funkce vraci CELOU kaskadu, ne jen prvni uroven: jestli ma nemovitost
 * panorama, se pozna az z odpovedi serveru (404), takze vykresleni
 * (`components/PropertyImage.tsx`) musi vedet, co prijde po nem. Diky tomu je
 * cele poradi testovatelne bez prohlizece a nejde ho obejit v komponente.
 *
 * ---------------------------------------------------------------------------
 * CO ZMIZELO A PROC
 *
 * UX v3 melo po mape adresy jeste uroven „VYREZ OBCE" (stred obce bez bodu,
 * krok DEMO2). TAB1 ji vypnul na kartach zakazek (nalez R.B. 3. 10.:
 * „Vrsovicka 74" ukazovala Stare Mesto, tedy stred Prahy); UX v4 ji zrusil
 * UPLNE — stred obce neni poloha nemovitosti a obrazek to nesmi predstirat.
 *
 * Krok UXF1 odsud vyhodil RASTROVY VYREZ CUZK (`CaseOrtho`: ortofoto + WMS
 * katastralni mapa). Nalez R.B. 7. 10. 2026: na karte 280x172 px je
 * „pixelovany se silnymi cernymi carami" — WMS kresli hranice parcel do
 * RASTRU v meritku vyrezu, takze zmenseni na velikost karty z nich udela
 * silne schody. Tytez vrstvy zustavaji tam, kde maji meritko a plochu:
 * v mapove karte zakazky (M1) a ve vyrezu do reportu (K2).
 * ---------------------------------------------------------------------------
 */

export interface PropertyImageInput {
  /** Fotka zakazky/nemovitosti (titulni), kdyz uz nejaka je. */
  readonly photoUrl: string | null;
  /**
   * Nemovitost, ke ktere server dopocita adresni bod (RUIAN) a zamcenou
   * geometrii parcel. Klient souradnice NEPOSILA — proxy je prijima jen jako
   * id, aby z administrace neslo vyrobit dotaz na libovolny bod mapy.
   */
  readonly propertyId?: string | null | undefined;
  /**
   * Souradnice adresy. Slouzi uz JEN jako priznak „adresa je geokodovana" —
   * bez nej nema server kam mapu umistit a kaskada rovnou konci vetou.
   */
  readonly lat: number | null;
  readonly lon: number | null;
  /**
   * Smi se misto fotky sahnout na mapove zdroje? `false` = jen fotka, jinak
   * rovnou neutralni plocha (pouziva se tam, kde by se vyrez nacital zbytecne).
   */
  readonly allowMap?: boolean | undefined;
}

export type PropertyImageSource =
  | { readonly kind: 'photo'; readonly url: string }
  | { readonly kind: 'panorama'; readonly propertyId: string }
  | { readonly kind: 'aerial'; readonly propertyId: string }
  | { readonly kind: 'none' };

/**
 * Kaskada zdroju obrazku nemovitosti, od nejlepsiho. Posledni polozka je VZDY
 * `none` (veta „Fotka po nahrání") — vykresleni tak nikdy nezustane bez
 * zalohy. Poradi NEMENI volajici; jedine, co rozhoduje, je `allowMap`.
 */
export function propertyImageCascade(input: PropertyImageInput): readonly PropertyImageSource[] {
  const sources: PropertyImageSource[] = [];
  if (input.photoUrl !== null && input.photoUrl !== '') {
    sources.push({ kind: 'photo', url: input.photoUrl });
  }
  const propertyId = input.propertyId ?? null;
  const hasPoint = input.lat !== null && input.lon !== null;
  if (input.allowMap !== false && propertyId !== null && propertyId !== '' && hasPoint) {
    sources.push({ kind: 'panorama', propertyId }, { kind: 'aerial', propertyId });
  }
  sources.push({ kind: 'none' });
  return sources;
}

/** Nejlepsi dostupny zdroj — prvni polozka kaskady. */
export function propertyImageSource(input: PropertyImageInput): PropertyImageSource {
  return propertyImageCascade(input)[0] ?? { kind: 'none' };
}
